import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Cropper from "react-easy-crop";
import { Modal, Box, Slider, Button } from "@mui/material";
import Spinner from "../../Spinner";
import { SendPostRequest } from "../../../http";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

function UpdateProfile({ fetchProfileData, onTabSelect }) {
  const fileInputRef = useRef(null);
  const cnicInputRef = useRef(null);
  const contactNumberInputRef = useRef(null);

  const patientProfile = useSelector((store) => store.global.patientProfile);
  const patientData = useSelector((store) => store.patient.patientLogin);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({
    contact_number: null,
    image: null,
    height: null,
    waist: null,
    neck: null,
    hip: null
  });

  // Image states
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [profileImageBase64, setProfileImageBase64] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);

  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    father_name: "",
    contact_number: "",
    gender: "",
    date_of_birth: "",
    cnic: "",
    profession: "",
    marital_status: "",
    blood_group: "",
    city: "",
    profile_img: "",
    address: "",
    height: "",
    waist: "",
    neck: "",
    hip: "",
  });

  useEffect(() => {
    if (patientProfile) {
      setProfileData((prev) => ({ ...prev, ...patientProfile }));
      if (patientProfile.profile_img) {
        setImagePreviewUrl(patientProfile.profile_img);
      }
    }
  }, [patientProfile]);

  // ---------- Select options ----------
  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const maritalOptions = [
    { value: "Married", label: "Married" },
    { value: "Single", label: "Single" },
    { value: "Divorced", label: "Divorced" },
  ];

  const bloodgroupOptions = [
    { value: "Unknown", label: "Unknown" },
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
  ];

  // ---------- Image Cropping Functions ----------
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const showCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(originalImage, croppedAreaPixels);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result;
        const base64 = String(base64data).split(",")[1] || null;
        setImagePreviewUrl(base64data);
        setProfileImageBase64(base64);
        setProfileData((p) => ({ ...p, profile_img: "" }));
        setCropModalOpen(false);
      };
      reader.readAsDataURL(croppedImage);
    } catch (e) {
      console.error('Error creating cropped image', e);
      toast.error("Error cropping image");
    }
  };

  // ---------- Image upload handling ----------
  const acceptTypes = ["image/jpeg", "image/png", "image/webp"];
  const maxBytes = 2 * 1024 * 1024; // 2 MB

  const basicFileChecks = (file) => {
    if (!file) return false;
    if (!acceptTypes.includes(file.type)) {
      const msg = "Only JPG, PNG, or WEBP images are allowed.";
      setError((p) => ({ ...p, image: msg }));
      toast.error(msg);
      return false;
    }
    if (file.size > maxBytes) {
      const msg = "File too large. Max 2MB allowed.";
      setError((p) => ({ ...p, image: msg }));
      toast.error(msg);
      return false;
    }
    setError((p) => ({ ...p, image: null }));
    return true;
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!basicFileChecks(file)) return;

    try {
      const reader = new FileReader();
      reader.onload = () => {
        setOriginalImage(reader.result);
        setCropModalOpen(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Error reading file:", err);
      toast.error("Unable to read selected file");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (!basicFileChecks(file)) return;

      const reader = new FileReader();
      reader.onload = () => {
        setOriginalImage(reader.result);
        setCropModalOpen(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Remove image
  const handleRemoveImage = () => {
    setImagePreviewUrl(null);
    setProfileImageBase64("REMOVE_IMAGE"); // Special value to indicate removal
    setProfileData((p) => ({ ...p, profile_img: "" }));
    setError((p) => ({ ...p, image: null }));
  };

  // ---------- Form validation and submission ----------
  const validateContactNumber = () => profileData?.contact_number?.length === 11;

  const validateAutoCalculationFields = () => {
    const fields = [
      { name: "height", min: 20, max: 110, label: "Height" },
      { name: "waist", min: 15, max: 120, label: "Waist" },
      { name: "neck", min: 5, max: 25, label: "Neck" }
    ];

    if (profileData.gender === "Female") {
      fields.push({ name: "hip", min: 10, max: 100, label: "Hip" });
    }

    let isValid = true;
    const newErrors = { ...error };

    for (const field of fields) {
      if (profileData[field.name]) {
        const value = parseFloat(profileData[field.name]);

        if (isNaN(value) || !Number.isInteger(value)) {
          newErrors[field.name] = `${field.label} must be a whole number`;
          isValid = false;
        } else if (value < field.min) {
          newErrors[field.name] = `${field.label} must be at least ${field.min} inches`;
          isValid = false;
        } else if (value > field.max) {
          newErrors[field.name] = `${field.label} must be at most ${field.max} inches`;
          isValid = false;
        } else {
          newErrors[field.name] = null;
        }
      } else {
        newErrors[field.name] = null;
      }
    }

    setError(newErrors);
    return isValid;
  };

  const validate = () => {
    const contactNumberValid = validateContactNumber();
    const autoCalcValid = validateAutoCalculationFields();

    if (!contactNumberValid) {
      contactNumberInputRef.current?.focus();
    }

    setError((prev) => ({
      ...prev,
      contact_number: contactNumberValid ? null : "Invalid Contact Number",
    }));

    return contactNumberValid && autoCalcValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;
    let errorMsg = null;

    if (name === "contact_number") {
      updatedValue = value.replace(/[^\d]/g, "").slice(0, 11);
      errorMsg = updatedValue.length < 11 ? "Invalid Contact Number" : null;
    }

    // Handle measurement fields (height, waist, neck, hip)
    if (["height", "waist", "neck", "hip"].includes(name)) {
      // Only allow numbers
      updatedValue = value.replace(/[^\d]/g, '');

      // Validate limits if value exists
      if (updatedValue) {
        const numValue = parseInt(updatedValue);

        if (name === "height" && (numValue < 20 || numValue > 110)) {
          errorMsg = "Height must be between 20 and 110";
        } else if (name === "waist" && (numValue < 15 || numValue > 120)) {
          errorMsg = "Waist must be between 15 and 120";
        } else if (name === "neck" && (numValue < 5 || numValue > 25)) {
          errorMsg = "Neck must be between 5 and 25";
        } else if (name === "hip" && (numValue < 10 || numValue > 100)) {
          errorMsg = "Hip must be between 10 and 100";
        }
      }
    }

    setError((prevError) => ({ ...prevError, [name]: errorMsg }));
    setProfileData((prevData) => ({
      ...prevData,
      [name]: updatedValue,
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setProfileData((prev) => ({
      ...prev,
      [name]: selectedOption?.value || ""
    }));
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const formData = { ...profileData };

      // Handle profile image - either new cropped image or removal
      if (profileImageBase64 === "REMOVE_IMAGE") {
        formData.profile_img = ""; // Empty string to remove image
      } else {
        formData.profile_img = profileImageBase64 || profileData.profile_img || "";
      }

      const response = await SendPostRequest("healthcare/update-profile", {
        host: "drburhan.clinta.biz",
        logged_id: patientData?.logged_id,
        form_data: formData,
      });

      setLoading(false);
      await fetchProfileData();

      if (response?.status === "success") {
        toast.success(response.message || "Profile updated successfully");
        onTabSelect?.("WeightManagment");
      } else {
        toast.error(response?.message || "Failed to update profile");
        console.error("Failed to update profile:", response?.error || "Unknown error");
      }
    } catch (err) {
      setLoading(false);
      toast.error("Error updating profile");
      console.error("Error updating profile:", err);
    }
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '95%' : "80%",
    maxWidth: isMobile ? '800%' : "800px",
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: isMobile ? 2 : 4,
    borderRadius: '8px',
    outline: 'none'
  };


  return (
    <div>
      {loading && (
        <div className="d-flex justify-content-center">
          <Spinner />
        </div>
      )}

      {/* Crop Modal using Material-UI */}
      <Modal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        aria-labelledby="crop-modal-title"
        aria-describedby="crop-modal-description"
      >
        <Box sx={modalStyle}>
          {/* Header with Close Icon */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h3 id="crop-modal-title" style={{ margin: 0 }}>
              Set Profile Picture
            </h3>
            <IconButton className="update-profile-img-modal-close-btn" onClick={() => setCropModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          {/* Cropper */}
          <div style={{ position: 'relative', height: '320px', background: '#333' }}>
            <Cropper
              image={originalImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>

          {/* Zoom Slider */}
          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              Zoom: {zoom.toFixed(2)}x
            </label>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e, newValue) => setZoom(newValue)}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', gap: '10px' }}>
            <Button
              className="btn btn-default"
              variant="outlined"
              onClick={() => setCropModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              variant="contained"
              onClick={showCroppedImage}
            >
              Save Image
            </Button>
          </div>
        </Box>
      </Modal>

      {!loading && (
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <h4 className="cs_fs_20 cs_medium mb-2">Update Profile</h4>

              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* First Name */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      First Name
                    </label>
                    <input
                      required
                      type="text"
                      className="updateprofile_input"
                      placeholder="Enter First Name"
                      name="first_name"
                      value={profileData?.first_name || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Last Name */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Last Name
                    </label>
                    <input
                      required
                      type="text"
                      className="updateprofile_input"
                      placeholder="Enter Last Name"
                      name="last_name"
                      value={profileData?.last_name || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Father Name */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Father Name
                    </label>
                    <input
                      type="text"
                      className="updateprofile_input"
                      placeholder="Enter Father Name"
                      name="father_name"
                      value={profileData?.father_name || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Contact Number */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Contact Number
                    </label>
                    <input
                      required
                      type="tel"
                      inputMode="numeric"
                      className={`updateprofile_input ${error?.contact_number ? "border-danger" : ""
                        }`}
                      placeholder="03XXXXXXXXX"
                      name="contact_number"
                      value={profileData?.contact_number || ""}
                      onChange={handleInputChange}
                      ref={contactNumberInputRef}
                    />
                    {error?.contact_number && (
                      <small className="text-danger">{error.contact_number}</small>
                    )}
                  </div>

                  {/* Gender */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Gender
                    </label>
                    <Select
                      name="gender"
                      className="updateprofile_select"
                      placeholder="Select"
                      options={genderOptions}
                      value={
                        profileData?.gender
                          ? { label: profileData.gender, value: profileData.gender }
                          : null
                      }
                      onChange={(opt) => handleSelectChange("gender", opt)}
                    />
                  </div>

                  {/* Date of Birth */}
                  <div className="col-lg-6 mb-3">
                    <label className="cs_input_label cs_heading_color fw-bold" style={{ marginBottom: "6px", display: "block" }}>
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      className="updateprofile_input"
                      name="date_of_birth"
                      value={profileData?.date_of_birth || ""}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        const value = e.target.value;
                        setProfileData((prev) => ({
                          ...prev,
                          date_of_birth: value,
                        }));
                      }}
                    />
                  </div>

                  {/* Identification Number (CNIC) */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Identification Number
                    </label>
                    <input
                      type="text"
                      className="updateprofile_input"
                      placeholder="Enter Identification Number"
                      name="cnic"
                      value={profileData?.cnic || ""}
                      onChange={handleInputChange}
                      ref={cnicInputRef}
                    />
                  </div>

                  {/* Profession */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Profession
                    </label>
                    <input
                      type="text"
                      className="updateprofile_input"
                      placeholder="Enter Profession"
                      name="profession"
                      value={profileData?.profession || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Marital Status */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Marital Status
                    </label>
                    <Select
                      name="marital_status"
                      className="updateprofile_select"
                      placeholder="Select"
                      options={maritalOptions}
                      value={
                        profileData?.marital_status
                          ? {
                            label: profileData.marital_status,
                            value: profileData.marital_status,
                          }
                          : null
                      }
                      onChange={(opt) => handleSelectChange("marital_status", opt)}
                    />
                  </div>

                  {/* Blood Group */}
                  <div className="col-lg-6 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Blood Group
                    </label>
                    <Select
                      name="blood_group"
                      className="updateprofile_select"
                      placeholder="Select"
                      options={bloodgroupOptions}
                      value={
                        profileData?.blood_group
                          ? {
                            label: profileData.blood_group,
                            value: profileData.blood_group,
                          }
                          : null
                      }
                      onChange={(opt) => handleSelectChange("blood_group", opt)}
                    />
                  </div>

                  {/* City */}
                  {!isMobile ? (
                    <div className="col-lg-6 mb-3">
                      <label
                        className="cs_input_label cs_heading_color fw-bold"
                        style={{ marginBottom: "6px", display: "block" }}
                      >
                        City
                      </label>
                      <input
                        type="text"
                        className="updateprofile_input"
                        placeholder="Enter City"
                        name="city"
                        value={profileData?.city || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  ) : null}


                  {/* Image Upload UX */}
                  <div className="col-lg-6 ">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Profile Image
                    </label>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="d-none"
                      name="profile_img"
                      onChange={handleFileChange}
                      accept={acceptTypes.join(",")}
                    />

                    {/* Upload box (clickable) */}
                    <div
                      role="button"
                      tabIndex={0}
                      className="updateprofile_dropzone d-flex flex-column align-items-center justify-content-center rounded p-2 text-center profile-drag-drop-img"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onClick={openFilePicker}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") openFilePicker();
                      }}
                      style={{
                        cursor: "pointer",
                        background: "#fafafa",
                        border: error?.image ? "1px solid #dc3545" : "1px dashed #ccc",
                        padding: "12px",
                      }}
                    >
                      <p className="mb-0 text-muted small">
                        Drag & drop an image here, or click to select
                      </p>
                      <small className="text-muted">Allowed: JPG / PNG / WEBP. Max 2MB</small>
                      {error?.image && <small className="text-danger d-block mt-1">{error.image}</small>}
                    </div>

                    {/* Preview */}
                    {imagePreviewUrl ? (
                      <div className="mt-3 text-center position-relative d-inline-block">
                        <img
                          src={imagePreviewUrl}
                          alt="Preview"
                          className="img-fluid rounded border w-100"
                          style={{ maxHeight: "120px", }}
                        />
                        {/* Cross Icon */}
                        <span
                          onClick={handleRemoveImage}
                          className="profile-previews-img-remove"
                          style={{
                            position: "absolute",
                            top: "-10px",
                            right: "-10px",
                            background: "#dc3545",
                            color: "white",
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "18px",
                            fontWeight: "bold"
                          }}
                        >
                          ×
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 small text-muted">No image selected</div>
                    )}

                  </div>

                  {/* Address */}
                  {isMobile ? (
                    <div className="col-lg-6 mb-3">
                      <label
                        className="cs_input_label cs_heading_color fw-bold"
                        style={{ marginBottom: "6px", display: "block" }}
                      >
                        City
                      </label>
                      <input
                        type="text"
                        className="updateprofile_input"
                        placeholder="Enter City"
                        name="city"
                        value={profileData?.city || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  ) : null}
                  <div className="col-lg-12 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Address
                    </label>
                    <input
                      type="text"
                      className="updateprofile_input"
                      placeholder="Enter Address"
                      name="address"
                      value={profileData?.address || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Auto Calculation copy */}
                  <div className="col-12 mb-2">
                    <h4 className="cs_fs_20 cs_medium mb-1">Auto Calculation</h4>
                    <p className="mb-2">
                      These parameters are required for auto-calculation of your body fat
                      percentage, BMI and other indicators on your dashboard.
                    </p>
                  </div>

                  {/* Height */}
                  <div className="col-lg-4 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Height in Inches
                    </label>
                    <input
                      type="number"
                      min="20"
                      max="110"
                      step="1"
                      className={`updateprofile_input ${error?.height ? "border-danger" : ""}`}
                      placeholder="20-110"
                      name="height"
                      value={profileData?.height || ""}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'Subtract' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }}
                    />
                    {error?.height && (
                      <small className="text-danger">{error.height}</small>
                    )}
                  </div>

                  {/* Waist */}
                  <div className="col-lg-4 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Waist in Inches
                    </label>
                    <input
                      type="number"
                      min="15"
                      max="120"
                      step="1"
                      className={`updateprofile_input ${error?.waist ? "border-danger" : ""}`}
                      placeholder="15-120"
                      name="waist"
                      value={profileData?.waist || ""}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'Subtract' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }}
                    />
                    {error?.waist && (
                      <small className="text-danger">{error.waist}</small>
                    )}
                  </div>

                  {/* Neck */}
                  <div className="col-lg-4 mb-3">
                    <label
                      className="cs_input_label cs_heading_color fw-bold"
                      style={{ marginBottom: "6px", display: "block" }}
                    >
                      Neck in Inches
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="25"
                      step="1"
                      className={`updateprofile_input ${error?.neck ? "border-danger" : ""}`}
                      placeholder="5-25"
                      name="neck"
                      value={profileData?.neck || ""}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'Subtract' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }}
                    />
                    {error?.neck && (
                      <small className="text-danger">{error.neck}</small>
                    )}
                  </div>

                  {/* Hip - only show for females */}
                  {profileData?.gender === "Female" && (
                    <div className="col-lg-4 mb-3">
                      <label
                        className="cs_input_label cs_heading_color fw-bold"
                        style={{ marginBottom: "6px", display: "block" }}
                      >
                        Hip in Inches
                      </label>
                      <input
                        type="number"
                        min="10"
                        max="100"
                        step="1"
                        className={`updateprofile_input ${error?.hip ? "border-danger" : ""}`}
                        placeholder="10-100"
                        name="hip"
                        value={profileData?.hip || ""}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'Subtract' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {error?.hip && (
                        <small className="text-danger">{error.hip}</small>
                      )}
                    </div>
                  )}

                  {/* Submit */}
                  <div className="col-lg-12 d-flex justify-content-end">
                    <button type="submit" className="update_profile_btn  mt-2">
                      Submit
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateProfile;